import { useEffect, useState, useCallback, useRef } from "react";
import Select from "react-select";
import RadarPlot from "../components/RadarPlot";
import {
  formateTimeRange,
  saveToState,
  loadFromState,
  resetState,
} from "../utils";
import { getProfiles } from "../api/quontxApi";

const RadarPlotFilters = ({
  timestampBuckets,
  domains,
  entities,
  proxies,
  isLoading,
  propagnateData,
}) => {
  const maxDomainCount = 12;
  const maxEntityCount = 5;
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [activeTimestampBucket, setActiveTimestampBucket] = useState(null);
  const [activeDomains, setActiveDomains] = useState([]);
  const [activeEntities, setActiveEntities] = useState([]);
  const [activeProxies, setActiveProxies] = useState([]);

  const [domainSelectDisabled, setDomainSelectDisabled] = useState(false);
  const [entitySelectDisabled, setEntitySelectDisabled] = useState(false);
  const [proxySelectDisabled, setProxySelectDisabled] = useState(false);

  const manageActiveDomains = (selectedDomains) => {
    if (selectedDomains.length <= maxDomainCount) {
      setDomainSelectDisabled(false);
      setActiveDomains(
        selectedDomains.map((domain) => {
          return { label: domain.label, value: domain.value };
        })
      );
      return;
    }
    setDomainSelectDisabled(true);
  };

  const manageActiveEntities = (selectedEntities) => {
    if (
      selectedEntities.length + (activeProxies ? activeProxies.length : 0) <=
      maxEntityCount
    ) {
      setActiveEntities(
        selectedEntities.map((entity) => {
          return { label: entity.label, value: entity.value };
        })
      );
    }
  };

  const manageActiveProxies = (selectedEntities) => {
    if (
      selectedEntities.length + (activeProxies ? activeProxies.length : 0) <=
      maxEntityCount
    ) {
      setActiveProxies(
        selectedEntities.map((entity) => {
          return { label: entity.label, value: entity.value };
        })
      );
    }
  };

  const resetFilters = () => {
    resetState("radarPlotState", [
      {
        stateFunc: setActiveTimestampBucket,
        nullVal: null,
      },
      {
        stateFunc: setActiveDomains,
        nullVal: [],
      },
      {
        stateFunc: setActiveEntities,
        nullVal: [],
      },
      {
        stateFunc: setActiveProxies,
        nullVal: [],
      },
    ]);
    setDomainSelectDisabled(false);
    setEntitySelectDisabled(false);
    setProxySelectDisabled(false);
  };

  const loadProfiles = useCallback(async () => {
    const profiles = await getProfiles({
      itemsPerPage: maxEntityCount,
      entities: [
        ...activeEntities.map((entities) => entities.value),
        ...activeProxies.map((proxies) => proxies.value),
      ],
    });
    propagnateFunctionRef.current(profiles.data);

    // Create key value pairs for the data
    profiles.data.data.forEach((profile) => {
      data[profile.metadata.corpora.name] = profile.profile;
    });
    setData(data);
  }, [activeEntities, activeProxies, data]);

  useEffect(() => {
    if (
      (activeEntities ? activeEntities?.length : 0) +
        (activeProxies ? activeProxies?.length : 0) <
      maxEntityCount
    ) {
      setEntitySelectDisabled(false);
      setProxySelectDisabled(false);
    } else {
      setEntitySelectDisabled(true);
      setProxySelectDisabled(true);
    }
  }, [
    activeEntities,
    activeProxies,
    setEntitySelectDisabled,
    setProxySelectDisabled,
  ]);

  useEffect(() => {
    loadFromState("radarPlotState", [
      {
        stateFunc: setActiveTimestampBucket,
        storageObjectKey: "activeTimestampBucket",
        defaultVal: null,
      },
      {
        stateFunc: setActiveDomains,
        storageObjectKey: "activeDomains",
        defaultVal: [],
      },
      {
        stateFunc: setActiveEntities,
        storageObjectKey: "activeEntities",
        defaultVal: [],
      },
      {
        stateFunc: setActiveProxies,
        storageObjectKey: "activeProxies",
        defaultVal: [],
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  useEffect(() => {
    if (
      activeTimestampBucket !== null ||
      activeDomains.length > 0 ||
      activeEntities.length > 0 ||
      activeProxies.length > 0
    ) {
      saveToState("radarPlotState", {
        activeTimestampBucket: activeTimestampBucket,
        activeDomains: activeDomains,
        activeEntities: activeEntities,
        activeProxies: activeProxies,
      });
    }
  }, [activeTimestampBucket, activeDomains, activeEntities, activeProxies]);

  useEffect(() => {
    if (
      activeEntities.length + (activeProxies ? activeProxies.length : 0) ===
      0
    )
      return;
    (async () => {
      setLoadingData(true);
      await loadProfiles();
      setLoadingData(false);
    })();
  }, [activeEntities, activeProxies, loadProfiles]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}
      {!isLoading && entities && proxies && domains && (
        <div>
          <div className="flex flex-col gap-4 items-center justify-center mb-8">
            <div className="flex gap-4  justify-center mb-8">
              <div className="w-[240px] flex flex-col gap-2">
                <p>Organization Profiles</p>
                <Select
                  isMulti={true}
                  isSearchable={true}
                  value={activeEntities}
                  onChange={(selected) => manageActiveEntities(selected)}
                  options={entities.map((entity) => {
                    return {
                      label: entity.name,
                      value: entity.uuid,
                      isDisabled: entitySelectDisabled,
                    };
                  })}
                />
              </div>
              <div className="w-[240px] flex flex-col gap-2">
                <p> Organization Proxiles</p>
                <Select
                  isMulti={true}
                  isSearchable={true}
                  value={activeProxies}
                  onChange={(selected) => manageActiveProxies(selected)}
                  options={proxies.map((proxy) => {
                    return {
                      label: proxy.name,
                      value: proxy.uuid,
                      isDisabled: proxySelectDisabled,
                    };
                  })}
                />
              </div>
            </div>
            <div className="flex gap-4 justify-center mb-8">
              <div className="w-[240px] flex flex-col gap-2">
                <p>Societal Domains</p>
                <Select
                  isDisabled={
                    (activeEntities === null && activeProxies === null) ||
                    (activeEntities?.length === 0 &&
                      activeProxies?.length === 0)
                  }
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor:
                        (activeEntities === null && activeProxies === null) ||
                        (activeEntities?.length === 0 &&
                          activeProxies?.length === 0)
                          ? "red"
                          : "grey",
                    }),
                  }}
                  isMulti={true}
                  isSearchable={true}
                  value={activeDomains}
                  onChange={(selected) => manageActiveDomains(selected)}
                  options={domains.map((domain) => {
                    return {
                      label: domain.description,
                      value: domain.uuid,
                      isDisabled: domainSelectDisabled,
                    };
                  })}
                />
              </div>
              <div className="w-[240px] flex flex-col gap-2">
                <p>Timestamp Bucket</p>
                <Select
                  isDisabled={
                    (activeEntities === null && activeProxies === null) ||
                    (activeEntities?.length === 0 &&
                      activeProxies?.length === 0)
                  }
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor:
                        (activeEntities === null && activeProxies === null) ||
                        (activeEntities?.length === 0 &&
                          activeProxies?.length === 0)
                          ? "red"
                          : "grey",
                    }),
                  }}
                  isSearchable={false}
                  value={{
                    label: activeTimestampBucket,
                    value: activeTimestampBucket,
                  }}
                  onChange={(selected) =>
                    setActiveTimestampBucket(selected.value)
                  }
                  options={timestampBuckets.map((timebucket, idx) => {
                    return {
                      label: formateTimeRange(timebucket),
                      value: timebucket,
                      isDisabled: idx < 10,
                    };
                  })}
                />
              </div>
            </div>
            <div>
              <button
                onClick={() => resetFilters()}
                className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
              >
                Reset filters
              </button>
            </div>
          </div>
          {activeDomains &&
            activeEntities &&
            activeProxies &&
            data &&
            activeTimestampBucket && (
              <div className="flex items-center content-center flex-wrap">
                <RadarPlot
                  isLoading={loadingData}
                  data={data}
                  timebucketIdx={timestampBuckets.indexOf(
                    activeTimestampBucket
                  )}
                  activeDomains={activeDomains}
                  activeEntities={activeEntities}
                  activeProxies={activeProxies}
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default RadarPlotFilters;
